import {
  Document,
  Event,
  EventAssignment,
  Int32,
  Invoice as InvoiceType,
  JobAdmin,
  JobNumberRequest,
  type JobTypeSubtype,
  NULL_TIME,
  Payment,
  Property,
  type Quotable,
  QuotableRead,
  QuoteLinePart,
  ServicesRendered,
  SQSEmail,
  SQSEmailAndDocument,
  User,
} from '@kalos/kalos-rpc';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  toast,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@kalos/ui';
import { CheckIcon, CopyIcon } from '@radix-ui/react-icons';
import { useNavigate } from '@tanstack/react-router';
import { addHours, format } from 'date-fns';
import { Check, MapPin, MoreVertical } from 'lucide-react';
import {
  type ComponentProps,
  type FC,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { useStore } from 'zustand';

import { OPTION_BLANK } from '../../../constants';
import { useAuth } from '../../../context/AuthContext';
import { useFeatureFlag } from '../../../featureFlags/useFeatureFlag';
import { useResolveEventTradeQuery } from '../../../hooks/react-query/useResolveEventTradeQuery';
import { useSubtypeTradesQuery } from '../../../hooks/react-query/useSubtypeTradesQuery';
import { useSpiffOptionBatchGetQuery } from '../../../hooks/react-query/useTaskClientServiceQuery';
import { useUserQuery } from '../../../hooks/react-query/useUserQuery';
import { useCopyToClipboard } from '../../../hooks/useCopyToClipboard';
import { customerHourlyRate, formatHourlyRate } from '../../../tools/customerBilling';
import {
  EventAssignmentClientService,
  EventClientService,
  InvoiceClientService,
  JobSubtypeClientService,
  JobTypeClientService,
  JobTypeSubtypeClientService,
  makeFakeRows,
  PaymentClientService,
  PropertyClientService,
  UserClientService,
} from '../../../tools/helpers';
import {
  buildSubtypeOptions,
  groupSubtypeOptions,
  isSubtypeRequired,
  SUBTYPE_REQUIRED_MESSAGE,
} from '../../../utils/eventTrade';
import { InvoicePaymentLinkGenerator } from '../../Invoice/components/InvoicePaymentLinkGenerator';
import { Loader } from '../../Loader/main';
import { ProposalProvider } from '../../Proposal/context/ProposalProvider';
import { ProposalModule } from '../../Proposal/main';
import { CallbackReview } from '../CallbackReview';
import { CustomerNotificationDialog } from '../CustomerInformation/NotificationDialog';
import { type Option } from '../Field';
import { Form, type Schema } from '../Form';
import { type Data, type Href, InfoTable } from '../InfoTable';
import { InvoicePaymentModule } from '../InvoicePayment/InvoicePayment';
import { Link as LinkComponent } from '../Link';
import { Modal } from '../Modal';
import { SectionBar } from '../SectionBar';
import { SellSheet } from '../SellSheet';
import { ServiceCallLogs } from '../ServiceCallLogs';
import { SpiffApplyComponent } from '../SpiffApplyComponent';
import { PieceWorkApplyForm } from '../SpiffApplyComponent/SpiffPieceWorkApply';
import { SERVICES_RENDERED_PAYMENT_INITIAL } from './components/constants';
import { ContractNumberDialog } from './components/ContractNumberDialog';
import { CustomerProposalWrapperByEvent } from './components/CustomerProposalWrapper';
import { Equipment } from './components/Equipment';
import { Invoice } from './components/Invoice';
import { JobNotFound } from './components/JobNotFound';
import { Notes } from './components/Notes';
import { createSpiffDialogStore } from './components/PieceWork/CreateSpiffOptionDialog.store';
import { PieceWorkOptionsTable } from './components/PieceWork/PieceWorkOptionsTable';
import { PropertyHistoryWrapper } from './components/PropertyHistoryWrapper';
import { Request } from './components/Request';
import { Services, type ServicesRenderedPaymentType } from './components/Services';
import { Spiffs } from './components/Spiffs';
import { reducer, type State } from './reducer';
import { getTabValueFromIndex } from './tabUtils';
import {
  filterEventFieldMaskToChangedFields,
  returnCorrectTimeField,
  returnLegacyDate,
} from './utils';

export type EventType = Event;
export type JobTypeSubtypeType = JobTypeSubtype;
export type ServicesRenderedType = ServicesRendered;

export interface Props {
  propertyId?: number;
  serviceCallId: number;

  onClose?: () => void;
  onSave?: (jobId?: number) => void;
  onSaving?: () => void;
  asProject?: boolean;
  projectParentId?: number;
  onLoaded?: () => void;
  showNotifications?: boolean;
  defaultTab?: number;
}

type EmailInvoice = {
  emailInvoice: boolean;
  paymentProcessor: 'helcim' | 'stripe';
};

const SCHEMA_EMAIL_INVOICE: Schema<EmailInvoice> = [
  [
    {
      label: 'Email Invoice to Customer?',
      name: 'emailInvoice',
      type: 'checkbox',
    },
  ],
  [
    {
      label: 'Payment Processor',
      name: 'paymentProcessor',
      options: [{ label: 'Helcim', value: 'helcim' }],
    },
  ],
];

// Sector has no default. 0 (Commercial) and 1 (Residential) are both real values, so "not yet
// chosen" needs an out-of-range sentinel rather than a falsy one.
const UNSET_SECTOR = -1;

const requestValidateFields = [
  'jobTypeId',
  'jobSubtypeId',
  'logPaymentType',
  'logJobStatus',
  'name',
  'departmentId',
  'description',
  'logTechnicianAssigned',
  'logPo',
  'servicesperformedrow1',
  'servicesperformedrow2',
  'servicesperformedrow3',
  'servicesperformedrow4',
  'leadGeneratedBy',
  'soldBy',
];

const CopyDataAction: FC<{ data: string; href?: boolean; hrefTag?: Href }> = ({
  data,
  href,
  hrefTag,
}) => {
  const [, copy] = useCopyToClipboard();
  const [isSuccess, setIsSuccess] = useState(false);

  const onCopy = async (event: React.MouseEvent<HTMLButtonElement>) => {
    try {
      event.preventDefault();
      const isSuccess = await copy(data);
      if (isSuccess) {
        setIsSuccess(isSuccess);

        setTimeout(() => {
          setIsSuccess(false);
        }, 2000);
      }
    } catch {
      console.error('Failed to copy transaction data');
    }
  };

  return (
    data && (
      <div className="flex w-full items-center justify-between gap-1">
        {href ? (
          <LinkComponent href={`${hrefTag}:${data}`}>{data}</LinkComponent>
        ) : (
          <span>{data}</span>
        )}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" onClick={onCopy}>
                <span className="sr-only">
                  {isSuccess ? 'Successfully copied data' : 'Copy data to clipboard'}
                </span>
                {isSuccess ? (
                  <CheckIcon className="size-4 text-green-500" />
                ) : (
                  <CopyIcon className="cursor-pointer" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>Copy data</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    )
  );
};

const handleCheckRequest = (
  data: Event,
  fields?: string[],
  requiresSubtype?: boolean,
  /** The saved job; Sub Type is checked on create or when Department / Sub Type change. */
  saved?: Pick<Event, 'departmentId' | 'jobSubtypeId'>,
): {
  requestErrors: { [key: string]: string };
  invoiceErrors: { label: string; message: string }[];
} => {
  const labels: { [key: string]: string } = {
    jobTypeId: 'Job Type',
    jobSubtypeId: 'Sub Type',
    logPaymentType: 'Payment Type',
    logJobStatus: 'Job Status',
    name: 'Name',
    departmentId: 'Department',
    description: 'Description',
    logTechnicianAssigned: 'Technician',
    logPo: 'Tracking Number',
    servicesperformedrow1: 'Services Performed (1)',
    servicesperformedrow2: 'Services Performed (2)',
    servicesperformedrow3: 'Services Performed (3)',
    servicesperformedrow4: 'Services Performed (4)',
    leadGeneratedBy: 'Lead Generated By',
    soldBy: 'Sold By',
    isResidential: 'Sector',
  };
  const requestErrors: { [key: string]: string } = {};
  if ((!fields || fields.includes('jobTypeId')) && !data.jobTypeId) {
    requestErrors.jobTypeId = 'This field is required';
  }
  if (
    (!fields || fields.includes('jobSubtypeId') || fields.includes('departmentId')) &&
    isSubtypeRequired(data.departmentId, requiresSubtype) &&
    !data.jobSubtypeId &&
    (!saved || data.departmentId !== saved.departmentId || data.jobSubtypeId !== saved.jobSubtypeId)
  ) {
    requestErrors.jobSubtypeId = SUBTYPE_REQUIRED_MESSAGE;
  }
  if ((!fields || fields.includes('logPaymentType')) && !data.logPaymentType.length) {
    requestErrors.logPaymentType = 'This field is required';
  }
  if ((!fields || fields.includes('logJobStatus')) && !data.logJobStatus.length) {
    requestErrors.logJobStatus = 'This field is required';
  }
  if ((!fields || fields.includes('name')) && !data.name.trim().replaceAll(' ', '').length) {
    requestErrors.name = 'This field is required';
  }
  if ((!fields || fields.includes('departmentId')) && !data.departmentId) {
    requestErrors.departmentId = 'This field is required';
  }
  if ((!fields || fields.includes('logPo')) && data.logPo.length > 1000) {
    requestErrors.logPo = 'This field cannot exceed 1000 characters in length';
  }
  if (
    (!fields || fields.includes('servicesperformedrow1')) &&
    data.servicesperformedrow1.length > 1000
  ) {
    requestErrors.servicesperformedrow1 = 'This field cannot exceed 1000 characters in length';
  }
  if (
    (!fields || fields.includes('servicesperformedrow2')) &&
    data.servicesperformedrow2.length > 1000
  ) {
    requestErrors.servicesperformedrow2 = 'This field cannot exceed 1000 characters in length';
  }
  if (
    (!fields || fields.includes('servicesperformedrow3')) &&
    data.servicesperformedrow3.length > 1000
  ) {
    requestErrors.servicesperformedrow3 = 'This field cannot exceed 1000 characters in length';
  }
  if (
    (!fields || fields.includes('servicesperformedrow4')) &&
    data.servicesperformedrow4.length > 1000
  ) {
    requestErrors.servicesperformedrow4 = 'This field cannot exceed 1000 characters in length';
  }
  if (
    (!fields || fields.includes('description')) &&
    !data.description.trim().replaceAll(' ', '').length
  ) {
    requestErrors.description = 'This field is required';
  }
  if (
    (!fields || fields.includes('isResidential')) &&
    // Checked against both real values rather than falsiness, since Commercial is 0 and
    // `!isResidential` would reject it.
    data.isResidential !== 0 &&
    data.isResidential !== 1
  ) {
    requestErrors.isResidential = 'This field is required';
  }

  const invoiceErrors = Object.entries(requestErrors).map(([label, message]) => ({
    label: labels[label],
    message,
  }));

  return { requestErrors, invoiceErrors };
};

export const ServiceCall: FC<Props> = ({
  asProject = false,
  onClose,
  onSave,
  onSaving,
  projectParentId,
  propertyId,
  serviceCallId: eventId,
  onLoaded,
  showNotifications = true,
  defaultTab = 0,
}) => {
  const auth = useAuth();
  const navigate = useNavigate();

  const loggedUserId = auth.user.id;

  const loggedUserQuery = useUserQuery({
    filters: {
      id: loggedUserId,
    },
    select(data) {
      return {
        ...data,
        hasBillingAccess: !!data.permissionGroups.find(
          ({ name }) => name === 'ServiceCallBillingAccess',
        ),
        isSpiffAdmin: !!data.permissionGroups.find(({ name }) => name === 'SpiffAdmin'),
        isArchiveAdmin: !!data.permissionGroups.find(({ name }) => name === 'ArchiveAdmin'),
        isWorkPieceAdmin: !!data.permissionGroups.find(({ name }) => name === 'PieceWorkAdmin'),
        isNewProposalTester: !!data.permissionGroups.find(
          ({ name }) => name === 'NewProposalTester',
        ),
        isNewContractUser: !!data.permissionGroups.find(({ name }) => name === 'NewContractUser'),
      };
    },
  });

  const workPieceQuery = useSpiffOptionBatchGetQuery({
    filter: {
      isActive: true,
      eventId,
    },
  });

  const initialState: State = {
    requestFields: [],
    tabIdx: 0,
    tabKey: 0,
    pendingSave: false,
    saveInvoice: false,
    sendInvoiceToCustomer: false,
    paymentProcessor: 'helcim',
    emailAlertOpen: false,
    serviceCallId: eventId || 0,
    entry: Event.create(),
    property: Property.create(),
    customer: User.create(),
    propertyEvents: [],
    loaded: false,
    loading: true,
    saving: false,
    payments: [],
    openSpiffApply: false,
    openPieceWorkApply: false,
    openCallbackReview: false,
    storedQuote: [],
    error: false,
    invoiceData: undefined,
    errorMessage: '',
    jobTypes: [],
    jobSubtypes: [],
    jobAdmins: [],
    jobTypeSubtypes: [],
    jobSubTypeOptions: [],
    servicesRendered: [],
    projects: [],
    parentId: null,
    confirmedParentId: null,
    contractData: undefined,
    projectData: Event.create(),
    openJobActivity: false,
    requestErrors: {},
    invoiceErrors: [],
    servicesRenderedPayment: SERVICES_RENDERED_PAYMENT_INITIAL,
    proposalsForm: undefined,
    servicesPendingQuotable: [],
    servicesPendingNewQuotable: [],
    servicesCreatingQuote: false,
    servicesEditing: SERVICES_RENDERED_PAYMENT_INITIAL,
    jobNotFound: false,
    excludedMaterialTotal: 0,
  };

  const [state, dispatch] = useReducer(reducer, initialState);
  const tradeQuery = useResolveEventTradeQuery({
    departmentId: state.entry.departmentId,
    jobTypeId: state.entry.jobTypeId,
    jobSubtypeId: state.entry.jobSubtypeId,
    isResidential: state.entry.isResidential,
  });
  const tradeResolution = tradeQuery.data;
  const subtypeTradeGroups = useFeatureFlag('job-forms.subtype-trade-groups');
  const subtypeTradesQuery = useSubtypeTradesQuery({
    departmentId: state.entry.departmentId,
    jobTypeId: state.entry.jobTypeId,
    isResidential: state.entry.isResidential,
    enabled: subtypeTradeGroups,
  });
  // The kept answer belongs to the fields before the last change; only the TRADE tag shows it
  const requiresSubtype = tradeQuery.isPlaceholderData
    ? undefined
    : tradeResolution?.requiresSubtype;

  const logJobNumber = state.entry.logJobNumber;
  const contractNumber = state.entry.contractNumber;
  const firstname = state.customer.firstname;
  const lastname = state.customer.lastname;
  const businessname = state.customer.businessname;
  const phone = state.customer.phone;
  const altphone = state.customer.altphone;
  const cellphone = state.customer.cellphone;
  const fax = state.customer.fax;
  const email = state.customer.email;
  const billingTerms = state.customer.billingTerms;
  const address = state.property.address;
  const city = state.property.city;
  const propertyState = state.property.state;
  const zip = state.property.zip;
  const customerId = state.customer.id;
  const requestRef = useRef(null);
  const [savedEntry, setSavedEntry] = useState<Pick<Event, 'departmentId' | 'jobSubtypeId'>>();
  const notificationTriggerRef = useRef<HTMLButtonElement>(null);
  const [openSellSheet, setOpenSellSheet] = useState<boolean>(false);
  const [openContractDialog, setOpenContractDialog] = useState<boolean>(false);

  const handleUpdateMaterialsStringAndCost = useCallback(async () => {
    const totalMaterials: Quotable[] = [];
    let totalCost = 0;
    let excludedCost = 0;
    let fullString = '';

    const materialReq = QuotableRead.create();
    materialReq.eventId = state.serviceCallId;
    materialReq.withoutLimit = true;

    const materials = await EventClientService.ReadQuotes(materialReq);

    if (materials) {
      if (materials.data.length > 0) {
        totalMaterials.concat(materials.data);
        for (let i = 0; i < state.servicesRendered.length; i++) {
          const date = state.servicesRendered[i].timeStarted;
          const tech = state.servicesRendered[i].name;
          const tempStringFirstPart = `${date}, - ${tech}`;
          const filteredMaterials = materials.data.filter(
            (material) => material.servicesRenderedId === state.servicesRendered[i].id,
          );
          if (filteredMaterials.length > 0) {
            let serviceRenderedMaterialString = tempStringFirstPart;
            for (let j = 0; j < filteredMaterials.length; j++) {
              const material = filteredMaterials[j];
              const tempStringSecondPart = ` - (${material.quantity})- ${material.description}- $${
                material.isBillable ? material.quotedPrice : 0
              }`;

              const cost = (material.isBillable ? material.quantity : 0) * material.quotedPrice;
              const taxAmount = 0;
              const markupAmount = 0;
              const qlReq = QuoteLinePart.create();
              qlReq.id = material.quoteLineId;
              /*
            try {
              const qlResult = await QuoteLinePartClientService.Get(qlReq);
              const tax = qlResult.tax;
              const markup = qlResult.markup;

              if (tax) {
                taxAmount = cost * tax - cost;
                console.log('Got tax', tax);
              }
              if (markup) {
                markupAmount = cost * markup - cost;
                console.log('got markup', markup);
              }
            } catch (err) {
              console.log('did not find quote line entry');
            }
            */
              totalCost += cost + markupAmount + taxAmount;
              if (material.excludeInvoiceDiscount && material.isBillable) {
                excludedCost += cost;
              }
              serviceRenderedMaterialString += tempStringSecondPart;
            }

            fullString += `${serviceRenderedMaterialString} \n `;
          }
        }
      }
    }

    const updateEvent = Event.create();
    updateEvent.id = state.serviceCallId;
    updateEvent.materialUsed = fullString;
    updateEvent.materialTotal = totalCost;
    updateEvent.fieldMask = ['MaterialUsed', 'MaterialTotal'];
    await EventClientService.Update(updateEvent);
    // Use MERGE_ENTRY to avoid overwriting optimistic updates (e.g. logJobStatus) with stale closure
    dispatch({
      type: 'MERGE_ENTRY',
      payload: { materialUsed: fullString, materialTotal: totalCost },
    });
    dispatch({ type: 'SET_EXCLUDED_MATERIAL_TOTAL', payload: excludedCost });
  }, [state.serviceCallId, state.servicesRendered]);

  const returnUpdatedMaterials = useCallback(
    async (servicesRendered: ServicesRendered[], serviceCallId: number) => {
      const totalMaterials: Quotable[] = [];
      let totalCost = 0;
      let excludedCost = 0;
      let fullString = '';

      const materialReq = QuotableRead.create();
      materialReq.eventId = serviceCallId;
      materialReq.withoutLimit = true;

      const materials = (await EventClientService.ReadQuotes(materialReq))!;

      if (materials) {
        totalMaterials.concat(materials.data);
        for (let i = 0; i < servicesRendered.length; i++) {
          const date = servicesRendered[i].timeStarted;
          const tech = servicesRendered[i].name;
          const tempStringFirstPart = `${date}, - ${tech}`;
          const filteredMaterials = materials.data.filter(
            (material) => material.servicesRenderedId === servicesRendered[i].id,
          );
          if (filteredMaterials.length > 0) {
            let serviceRenderedMaterialString = tempStringFirstPart;
            for (let j = 0; j < filteredMaterials.length; j++) {
              const material = filteredMaterials[j];
              const tempStringSecondPart = ` - (${material.quantity})- ${material.description}- $${
                material.isBillable ? material.quotedPrice : 0
              }`;

              const cost = (material.isBillable ? material.quantity : 0) * material.quotedPrice;
              const taxAmount = 0;
              const markupAmount = 0;
              const qlReq = QuoteLinePart.create();
              qlReq.id = material.quoteLineId;

              totalCost += cost + markupAmount + taxAmount;
              if (material.excludeInvoiceDiscount && material.isBillable) {
                excludedCost += cost;
              }
              serviceRenderedMaterialString += tempStringSecondPart;
            }

            fullString += `${serviceRenderedMaterialString} \n `;
          }
        }
      }

      return { materialString: fullString, totalCost: totalCost, excludedCost: excludedCost };
    },
    [],
  );

  const setCreateSpiffDialogStore = useStore(createSpiffDialogStore, (s) => s.setOpen);

  const toggleOpenSpiffApply = () => {
    dispatch({ type: 'SET_OPEN_SPIFF_APPLY', payload: !state.openSpiffApply });
  };
  const toggleOpenPieceWorkApply = () => {
    console.log('toggle open piece work apply');
    dispatch({ type: 'SET_OPEN_PIECE_WORK_APPLY', payload: !state.openPieceWorkApply });
  };
  const toggleOpenCallbackReview = () => {
    dispatch({
      type: 'SET_OPEN_CALLBACK_REVIEW',
      payload: !state.openCallbackReview,
    });
  };

  const toggleOpenJobActivity = () => {
    dispatch({
      type: 'SET_OPEN_JOB_ACTIVITY',
      payload: !state.openJobActivity,
    });
  };

  const updateServicesRenderedFromProp = useCallback((servicesRendered: ServicesRendered[]) => {
    dispatch({ type: 'SET_SERVICES_RENDERED', payload: servicesRendered });
  }, []);

  const loadPayments = useCallback(async () => {
    try {
      if (state.serviceCallId != 0 && state.serviceCallId != undefined) {
        const req = Int32.create();
        req.value = state.serviceCallId;
        const result = (await PaymentClientService.GetPaymentsByEventID(req))!.results;
        dispatch({ type: 'SET_PAYMENTS', payload: result });
        return result;
      }
    } catch (e) {
      console.log('failed to get payment data', e);
    }
    return [];
  }, [state.serviceCallId]);

  const customerQuery = useUserQuery({
    filters: {
      id: state.customer.id,
    },
  });

  const load = useCallback(async () => {
    if (typeof eventId === 'undefined' || isNaN(eventId)) {
      dispatch({ type: 'SET_JOB_NOT_FOUND', payload: true });
      dispatch({ type: 'SET_LOADED', payload: true });
      dispatch({ type: 'SET_LOADING', payload: false });
      return;
    }
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_SERVICE_CALL_ID', payload: eventId });

      if (state.serviceCallId != 0 && eventId != 0) {
        try {
          const jobData = await EventClientService.GetEventDetails(
            JobNumberRequest.create({ jobNumber: eventId }),
          );

          if (jobData && jobData.event && jobData.event.id) {
            const startTimeData = jobData.event.timeStarted ?? '';
            const endTimeData = jobData.event.timeEnded ?? '';
            const startSplit = startTimeData.split(':');
            const endSplit = endTimeData.split(':');
            const startDate = jobData.event.dateStarted ?? '';
            const endDate = jobData.event.dateEnded ?? '';
            const startTimeDate = startDate.split(' ')[0];
            const endTimeDate = endDate.split(' ')[0];
            const fullStartDate = `${startTimeDate} ${startSplit[0]}:${startSplit[1]}:00`;
            const fullEndDate = `${endTimeDate} ${endSplit[0]}:${endSplit[1]}:00`;
            const materialTotals = await returnUpdatedMaterials(
              jobData.servicesRendered,
              jobData.event.id,
            );
            jobData.event.materialUsed = materialTotals.materialString;
            jobData.event.materialTotal = materialTotals.totalCost;
            dispatch({ type: 'SET_EXCLUDED_MATERIAL_TOTAL', payload: materialTotals.excludedCost });
            jobData.event.dateStarted = fullStartDate;
            jobData.event.dateEnded = fullEndDate;

            dispatch({ type: 'SET_ENTRY', payload: jobData.event });
            setSavedEntry(jobData.event);
            dispatch({ type: 'SET_PROPERTY', payload: jobData.property ?? Property.create() });
            dispatch({ type: 'SET_CUSTOMER', payload: jobData.customer ?? User.create() });
            dispatch({
              type: 'SET_PROPERTY_EVENTS',
              payload: jobData.propertyEvent,
            });
            dispatch({ type: 'SET_JOB_TYPES', payload: jobData.jobTypes });
            dispatch({ type: 'SET_JOB_SUBTYPES', payload: jobData.jobSubtypes });
            dispatch({
              type: 'SET_JOB_TYPE_SUBTYPES',
              payload: jobData.jobTypesSubtypes,
            });
            dispatch({ type: 'SET_JOB_ADMINS', payload: jobData.jobAdmins });
            dispatch({
              type: 'SET_SERVICES_RENDERED',
              payload: jobData.servicesRendered,
            });
            dispatch({ type: 'SET_PAYMENTS', payload: jobData.payment });
            dispatch({ type: 'SET_INVOICE_DATA', payload: jobData.invoice });
            dispatch({ type: 'SET_LOADED', payload: true });
            dispatch({ type: 'SET_LOADING', payload: false });
            dispatch({ type: 'SET_CONTRACT_DATA', payload: undefined });
          } else {
            // Job not found
            dispatch({ type: 'SET_JOB_NOT_FOUND', payload: true });
            dispatch({ type: 'SET_LOADED', payload: true });
            dispatch({ type: 'SET_LOADING', payload: false });
          }
        } catch (err) {
          // Job not found or API error
          console.error(err);
          dispatch({ type: 'SET_JOB_NOT_FOUND', payload: true });
          dispatch({ type: 'SET_LOADED', payload: true });
          dispatch({ type: 'SET_LOADING', payload: false });
        }
      } else {
        const req = Event.create();
        req.isResidential = UNSET_SECTOR;
        const dateInit = new Date();
        req.dateStarted = format(dateInit, 'yyyy-MM-dd HH:mm:ss');
        req.dateEnded = format(dateInit, 'yyyy-MM-dd HH:mm:ss');
        const propertyInfo = await PropertyClientService.loadPropertyByID(
          propertyId || state.entry.propertyId,
        );

        if (propertyInfo) {
          req.name = `${propertyInfo.address} ${propertyInfo.city}, ${propertyInfo.state} ${propertyInfo.zip}`;
          const entry = req;
          const dateStart = new Date();
          dateStart.setHours(8);
          dateStart.setMinutes(0);
          dateStart.setSeconds(0);
          const dateEnd = addHours(dateStart, 10);
          const dateStartString = format(dateStart, 'yyyy-MM-dd HH:mm:ss');
          const endDateString = format(dateEnd, 'yyyy-MM-dd HH:mm:ss');
          entry.dateStarted = dateStartString;
          entry.dateEnded = endDateString;

          const customerDetails = await UserClientService.loadUserById(propertyInfo.userId);
          const propertyEventDetails = await EventClientService.loadEventsByPropertyId(
            propertyInfo.id,
          );
          const jobTypeList = await JobTypeClientService.loadJobTypes();
          const jobSubTypeList = await JobSubtypeClientService.loadJobSubtypes();
          const jobTypeSubtypesList = await JobTypeSubtypeClientService.loadJobTypeSubtypes();

          dispatch({ type: 'SET_ENTRY', payload: entry });
          dispatch({ type: 'SET_PROPERTY', payload: propertyInfo });
          dispatch({ type: 'SET_CUSTOMER', payload: customerDetails });
          dispatch({
            type: 'SET_PROPERTY_EVENTS',
            payload: propertyEventDetails,
          });
          dispatch({ type: 'SET_JOB_TYPES', payload: jobTypeList });
          dispatch({ type: 'SET_JOB_SUBTYPES', payload: jobSubTypeList! });
          dispatch({
            type: 'SET_JOB_TYPE_SUBTYPES',
            payload: jobTypeSubtypesList!,
          });
          dispatch({ type: 'SET_JOB_ADMINS', payload: [] });
          dispatch({
            type: 'SET_SERVICES_RENDERED',
            payload: [ServicesRendered.create()],
          });
          dispatch({ type: 'SET_PAYMENTS', payload: [Payment.create()] });
          dispatch({ type: 'SET_INVOICE_DATA', payload: InvoiceType.create() });
          dispatch({ type: 'SET_LOADED', payload: true });
          dispatch({ type: 'SET_LOADING', payload: false });
          dispatch({ type: 'SET_CONTRACT_DATA', payload: undefined });
        }
      }
    } catch (err) {
      console.log('error', err);
      dispatch({ type: 'SET_ERROR', payload: true });
      dispatch({ type: 'SET_ERROR_MESSAGE', payload: err as string });
      dispatch({ type: 'SET_LOADING', payload: false });
      dispatch({ type: 'SET_LOADED', payload: true });
    }
  }, [state.serviceCallId, eventId, propertyId, state.entry.propertyId, returnUpdatedMaterials]);

  const handleSave = useCallback(() => {
    const { requestErrors, invoiceErrors } = handleCheckRequest(
      state.entry,
      undefined,
      requiresSubtype,
      savedEntry,
    );
    dispatch({
      type: 'SET_REQUEST_ERRORS',
      payload: requestErrors,
    });
    dispatch({
      type: 'SET_INVOICE_ERRORS',
      payload: invoiceErrors,
    });
    if (!Object.keys(requestErrors).length) {
      console.log('Starting Save');
      dispatch({ type: 'SET_PENDING_SAVE', payload: true });
      dispatch({ type: 'SET_SAVE_INVOICE', payload: false });
      dispatch({ type: 'SET_SEND_INVOICE_TO_CUSTOMER', payload: false });
      dispatch({ type: 'SET_EMAIL_ALERT_OPEN', payload: false });
      dispatch({
        type: 'SET_TAB_IDX',
        payload: state.tabIdx !== 0 ? 0 : state.tabIdx,
      });
      dispatch({
        type: 'SET_TAB_KEY',
        payload: state.tabIdx !== 0 ? state.tabKey + 1 : state.tabKey,
      });
    }
  }, [state.entry, state.tabKey, state.tabIdx, requiresSubtype, savedEntry]);

  const handleSaveAndInvoice = useCallback(() => {
    // Same Sub Type check as Save Job Only before the invoice dialog opens
    const { requestErrors, invoiceErrors } = handleCheckRequest(
      state.entry,
      ['jobSubtypeId'],
      requiresSubtype,
      savedEntry,
    );
    if (Object.keys(requestErrors).length) {
      dispatch({ type: 'SET_REQUEST_ERRORS', payload: requestErrors });
      dispatch({ type: 'SET_INVOICE_ERRORS', payload: invoiceErrors });
      return;
    }
    dispatch({ type: 'SET_EMAIL_ALERT_OPEN', payload: true });
  }, [state.entry, requiresSubtype, savedEntry]);

  const saveServiceCall = useCallback(async () => {
    dispatch({ type: 'SET_SAVING', payload: true });
    if (onSaving) {
      onSaving();
    }
    dispatch({ type: 'SET_LOADING', payload: true });
    let jobId = null;
    const res = Event.clone(state.entry);
    if (res.totalamountrow1 == undefined || res.totalamountrow1 == '') {
      res.totalamountrow1 = '0';
    }
    if (res.totalamountrow2 == undefined || res.totalamountrow2 == '') {
      res.totalamountrow2 = '0';
    }
    if (res.totalamountrow3 == undefined || res.totalamountrow3 == '') {
      res.totalamountrow3 = '0';
    }
    if (res.totalamountrow4 == undefined || res.totalamountrow4 == '') {
      res.totalamountrow4 = '0';
    }
    if (res.discount == undefined || res.discount == '') {
      res.discount = '0';
    }
    if (res.discountcost == undefined || res.discountcost == '') {
      res.discountcost = '0';
    }

    try {
      if (state.serviceCallId) {
        // Auto-create invoice if payment status is Collected or Paid and no invoice exists
        // Only if not explicitly saving invoice (to avoid collision with "Save and Invoice" button)
        const paymentStatus = res.logPaymentStatus;
        const computedTotal =
          parseFloat(res.totalamountrow1 || '0') +
          parseFloat(res.totalamountrow2 || '0') +
          parseFloat(res.totalamountrow3 || '0') +
          parseFloat(res.totalamountrow4 || '0') +
          (res.materialTotal || 0) -
          parseFloat(res.discountcost || '0');
        const shouldAutoCreateInvoice =
          !state.saveInvoice &&
          (paymentStatus === 'Collected' || paymentStatus === 'Paid') &&
          computedTotal > 0 &&
          (!state.invoiceData || state.invoiceData.id === 0);

        const idArray = res.logTechnicianAssigned.split(',');
        let results: EventAssignment[] = [];
        console.log('ID array', idArray);
        try {
          const assignmentReq = EventAssignment.create();
          assignmentReq.eventId = res.id;
          const assignedEvents = await EventAssignmentClientService.BatchGet(assignmentReq);
          results = assignedEvents!.results;
        } catch {
          console.log('no one assigned, just create');
        }

        try {
          // Build helper collections for diff-ing
          const desiredIds = idArray.map((id) => Number(id)).filter((id) => id !== 0);
          const existingIds = results.map((a) => a.userId);

          // 1.  Create new assignments for technicians who are now assigned but were not previously
          for (const userId of desiredIds) {
            if (!existingIds.includes(userId)) {
              const assignment = EventAssignment.create();
              assignment.userId = userId;
              assignment.eventId = state.serviceCallId;
              await EventAssignmentClientService.Create(assignment);
            }
          }

          // 2.  Delete assignments for technicians who are no longer assigned
          for (const existing of results) {
            if (!desiredIds.includes(existing.userId)) {
              const assignmentToDelete = EventAssignment.create();
              assignmentToDelete.id = existing.id;
              await EventAssignmentClientService.Delete(assignmentToDelete);
            }
          }

          // 3.  If a technician is still assigned and still has a record, we do nothing (implicitly handled)
        } catch {
          console.log('error updating event assignment');
        }

        res.id = state.serviceCallId;

        if (res.fieldMask.length > 0) {
          if (res.fieldMask.find((el) => el == 'DateStarted')) {
            res.timeStarted = returnCorrectTimeField(res.dateStarted);
            res.dateStarted = returnLegacyDate(res.dateStarted);

            res.fieldMask.push('TimeStarted');
          }
          if (res.fieldMask.find((el) => el == 'DateEnded')) {
            res.timeEnded = returnCorrectTimeField(res.dateEnded);
            res.dateEnded = returnLegacyDate(res.dateEnded);

            res.fieldMask.push('TimeEnded');
          }

          let previousEvent: Event | null = null;
          try {
            const loaded = await EventClientService.loadEvent(res.id);
            if (loaded) previousEvent = loaded;
          } catch {
            previousEvent = null;
          }

          if (previousEvent) {
            res.fieldMask = filterEventFieldMaskToChangedFields(res.fieldMask, previousEvent, res);
          }

          await handleUpdateMaterialsStringAndCost();
          // Set billing date if invoicing (explicitly or auto-creating)
          if (state.saveInvoice == true || shouldAutoCreateInvoice) {
            //we are invoicing, so set a billing date
            if (res.logBillingDate == NULL_TIME || res.logBillingDate == '') {
              res.logBillingDate = res.dateEnded;

              res.fieldMask.push('LogBillingDate');
            }
          }
          console.log('We are updating', res);
          // The discount can change because of the total factor, so it's recomputed on
          // every save — but only write it when the stored value actually differs.
          // Unconditional writes made the backend's activity log show phantom changes
          // like 'Discount' from '' to '0' on saves that never touched the discount.
          const numericValue = (v: string | undefined) => {
            const n = parseFloat(v ?? '');
            return Number.isNaN(n) ? 0 : n;
          };
          const discountChanged =
            !previousEvent ||
            numericValue(previousEvent.discount) !== numericValue(res.discount) ||
            numericValue(previousEvent.discountcost) !== numericValue(res.discountcost);
          if (discountChanged) {
            res.fieldMask.push('Discount');
            res.fieldMask.push('DiscountCost');
          }

          // Ensure soldBy and leadGeneratedBy are valid integers
          res.soldBy = Number.isNaN(Number(res.soldBy)) ? 0 : Number(res.soldBy);
          res.leadGeneratedBy = Number.isNaN(Number(res.leadGeneratedBy))
            ? 0
            : Number(res.leadGeneratedBy);

          if (res.fieldMask.length > 0) {
            await EventClientService.Update(res);
          }
        }

        // If an invoice exists but we're just saving the job (not explicitly invoicing),
        // update the payment status and type to keep them in sync (only if those fields changed)
        const paymentFieldsChanged =
          res.fieldMask.includes('LogPaymentStatus') || res.fieldMask.includes('LogPaymentType');
        if (
          !state.saveInvoice &&
          !shouldAutoCreateInvoice &&
          state.invoiceData?.id &&
          paymentFieldsChanged
        ) {
          const invoice = InvoiceType.create();
          invoice.id = state.invoiceData.id;
          invoice.logPaymentStatus = res.logPaymentStatus;
          invoice.logPaymentType = res.logPaymentType;
          invoice.fieldMask = ['LogPaymentStatus', 'LogPaymentType'];
          try {
            await InvoiceClientService.Update(invoice);
          } catch (err) {
            console.log('we had an error updating the invoice payment status', err);
          }
        }

        // Use the same invoice creation logic whether explicitly saving or auto-creating
        if (state.saveInvoice == true || shouldAutoCreateInvoice) {
          const discountPercent = parseFloat(res.discount || '0');
          if (discountPercent > 0) {
            const { excludedCost } = await returnUpdatedMaterials(
              state.servicesRendered,
              state.serviceCallId,
            );
            const discountableBase =
              parseFloat(res.totalamountrow1 || '0') +
              parseFloat(res.totalamountrow2 || '0') +
              parseFloat(res.totalamountrow3 || '0') +
              parseFloat(res.totalamountrow4 || '0') +
              (res.materialTotal || 0) -
              excludedCost;
            res.discountcost = (discountableBase * (discountPercent / 100)).toFixed(2);
          }
          const invoice = InvoiceType.create();
          invoice.eventId = state.serviceCallId;
          invoice.contractId = res.contractId;
          invoice.propertyId = res.propertyId;
          invoice.propertyBilling = res.propertyBilling;
          invoice.notes = res.notes;
          invoice.servicesperformedrow1 = res.servicesperformedrow1;
          invoice.servicesperformedrow2 = res.servicesperformedrow2;
          invoice.servicesperformedrow3 = res.servicesperformedrow3;
          invoice.servicesperformedrow4 = res.servicesperformedrow4;
          invoice.totalamountrow1 = res.totalamountrow1;
          invoice.totalamountrow2 = res.totalamountrow2;
          invoice.totalamountrow3 = res.totalamountrow3;
          invoice.totalamountrow4 = res.totalamountrow4;
          invoice.userId = customerId;
          invoice.serviceItem = res.invoiceServiceItem;
          invoice.discount = res.discount;
          invoice.discountcost = res.discountcost;
          invoice.startDate = res.dateStarted;
          invoice.materialTotal = res.materialTotal.toString();
          invoice.materialUsed = res.materialUsed;
          const total1 = parseFloat(res.totalamountrow1);
          const total2 = parseFloat(res.totalamountrow2);
          const total3 = parseFloat(res.totalamountrow3);
          const total4 = parseFloat(res.totalamountrow4);

          const discountAmount = parseFloat(res.discountcost);
          invoice.serviceItem = res.invoiceServiceItem;
          const materialTotal = res.materialTotal;
          const grandTotal = total1 + total2 + total3 + total4 + materialTotal - discountAmount;
          invoice.startDate = res.logBillingDate;
          invoice.logPaymentStatus = res.logPaymentStatus;
          invoice.userId = state.customer.id;
          invoice.logPaymentType = res.logPaymentType;
          invoice.totalamounttotal = grandTotal.toString();
          if (state.invoiceData && state.invoiceData.id != 0) {
            invoice.id = state.invoiceData.id;
            invoice.fieldMask = [
              'EventId',
              'StartDate',
              'PropertyId',
              'PropertyBilling',
              'UserId',
              'Notes',
              'Servicesperformedrow1',
              'Servicesperformedrow2',
              'Servicesperformedrow3',
              'Servicesperformedrow4',
              'ServiceItem',
              'Totalamountrow1',
              'Totalamountrow2',
              'Totalamountrow3',
              'Totalamountrow4',
              'Totalamounttotal',
              'Discount',
              'Discountcost',
              'LogPaymentStatus',
              'LogPaymentType',
              'MaterialTotal',
              'MaterialUsed',
            ];
            try {
              await InvoiceClientService.Update(invoice);
            } catch (err) {
              console.log('we had an error updating the invoice', err);
            }
            if (state.saveInvoice || shouldAutoCreateInvoice) {
              const sqsInvoiceEmail = SQSEmailAndDocument.create();
              const sqsEmail = SQSEmail.create();
              const document = Document.create();
              document.invoiceId = invoice.id;
              document.propertyId = invoice.propertyId;
              document.userId = customerId;
              if (shouldAutoCreateInvoice) {
                sqsEmail.to = 'emailed.invoices@kalosflorida.com';
              } else {
                if (invoice.propertyBilling == 0) {
                  sqsEmail.to = state.customer.email;
                } else {
                  sqsEmail.to = state.property.email;
                }
                if (state.sendInvoiceToCustomer == false) {
                  sqsEmail.to = 'emailed.invoices@kalosflorida.com';
                }
              }
              sqsInvoiceEmail.document = document;
              sqsInvoiceEmail.email = sqsEmail;
              sqsInvoiceEmail.paymentType = state.paymentProcessor;
              try {
                await InvoiceClientService.SendSQSInvoiceEmail(sqsInvoiceEmail);
              } catch (err) {
                console.log('we had an error sending SQS invoice email', err);
              }
            }
          } else {
            //we need to create it

            const newInvoice = await InvoiceClientService.Create(invoice);
            if (newInvoice) {
              dispatch({ type: 'SET_INVOICE_DATA', payload: newInvoice });
            }
            if (state.saveInvoice || shouldAutoCreateInvoice) {
              const sqsInvoiceEmail = SQSEmailAndDocument.create();
              const sqsEmail = SQSEmail.create();
              const document = Document.create();
              document.invoiceId = newInvoice!.id;
              document.propertyId = newInvoice!.propertyId;
              document.userId = state.customer.id;
              if (shouldAutoCreateInvoice) {
                sqsEmail.to = 'emailed.invoices@kalosflorida.com';
              } else {
                if (invoice.propertyBilling == 0) {
                  sqsEmail.to = state.customer.email;
                } else {
                  sqsEmail.to = state.property.email;
                }
                if (state.sendInvoiceToCustomer == false) {
                  sqsEmail.to = 'emailed.invoices@kalosflorida.com';
                }
              }
              sqsInvoiceEmail.document = document;
              sqsInvoiceEmail.email = sqsEmail;
              sqsInvoiceEmail.paymentType = state.paymentProcessor;
              try {
                await InvoiceClientService.SendSQSInvoiceEmail(sqsInvoiceEmail);
              } catch (err) {
                console.log('we had an error sending SQS invoice email', err);
              }
            }
          }
          dispatch({ type: 'SET_SEND_INVOICE_TO_CUSTOMER', payload: false });
          dispatch({ type: 'SET_SAVE_INVOICE', payload: false });
        }
        jobId = state.serviceCallId;
      } else {
        const temp = Event.clone(state.entry);
        temp.propertyId = propertyId || state.entry.propertyId;
        temp.logVersion = 1;
        temp.timeStarted = returnCorrectTimeField(temp.dateStarted);
        temp.timeEnded = returnCorrectTimeField(temp.dateEnded);
        temp.dateStarted = returnLegacyDate(temp.dateStarted);
        temp.dateEnded = returnLegacyDate(temp.dateEnded);

        temp.discount = state.customer.discount.toString();
        if (projectParentId) {
          temp.parentId = projectParentId;
        }
        temp.soldBy = Number.isNaN(Number(temp.soldBy)) ? 0 : Number(temp.soldBy);
        temp.leadGeneratedBy = Number.isNaN(Number(temp.leadGeneratedBy))
          ? 0
          : Number(temp.leadGeneratedBy);
        const res = await EventClientService.Create(temp);
        if (!res) {
          toast({
            title: 'Failed to create an event!',
            description: 'Please try again.',
            variant: 'destructive',
          });
          throw new Error('Failed to create an event!');
        }
        jobId = res?.id;
        for (let i = 0; i < state.jobAdmins.length; i++) {
          const jobAdminReq = JobAdmin.clone(state.jobAdmins[i]);
          if (res && res.id != 0) {
            jobAdminReq.jobNumber = res.id;
            await EventClientService.CreateJobAdmin(jobAdminReq);
          }
        }
        const logNumber = `${format(new Date(), 'yy')}-${res!.id}`;
        const newEvent = Event.create();
        const idArray = temp.logTechnicianAssigned.split(',');
        let results: EventAssignment[] = [];

        try {
          const assignmentReq = EventAssignment.create();
          assignmentReq.eventId = res!.id;
          const assignedEvents = await EventAssignmentClientService.BatchGet(assignmentReq);
          results = assignedEvents!.results;
        } catch {
          console.log('no one assigned, just create');
        }
        try {
          for (const event in results) {
            const assignment = EventAssignment.create();
            assignment.id = results[event].id;
            EventAssignmentClientService.Delete(assignment);
          }
          for (const id in idArray) {
            const assignment = EventAssignment.create();
            assignment.userId = Number(idArray[id]);
            assignment.eventId = res!.id;
            await EventAssignmentClientService.Create(assignment);
          }
        } catch {
          console.log('error updating event assignment');
        }
        newEvent.id = res!.id;
        newEvent.logJobNumber = logNumber;
        newEvent.isResidential = temp.isResidential;
        newEvent.fieldMask = ['Id', 'LogJobNumber', 'IsResidential'];
        await EventClientService.Update(newEvent);
      }
      setSavedEntry({
        departmentId: state.entry.departmentId,
        jobSubtypeId: state.entry.jobSubtypeId,
      });
    } catch (err) {
      console.error(err);
    }

    if (!state.serviceCallId) {
      dispatch({ type: 'SET_SERVICE_CALL_ID', payload: res.id });
    }
    if (showNotifications) {
      toast({
        title: state.saveInvoice ? 'Job Saved and Invoiced' : 'Job Saved',
        variant: 'success',
      });
    }
    if (onSave && jobId) {
      console.log('Saved');
      onSave(jobId);
    }
    if (onClose) {
      console.log('Closing');
      onClose();
    } else {
      dispatch({ type: 'SET_SAVING', payload: false });
      dispatch({ type: 'SET_LOADING', payload: false });
    }

    dispatch({ type: 'SET_SAVING', payload: false });
    dispatch({ type: 'SET_LOADING', payload: false });
  }, [
    state.entry,
    state.serviceCallId,
    state.property,
    propertyId,
    state.customer,
    state.saveInvoice,
    projectParentId,
    state.invoiceData,
    customerId,
    state.sendInvoiceToCustomer,
    state.jobAdmins,
    state.paymentProcessor,
    onSave,
    onSaving,
    onClose,
    handleUpdateMaterialsStringAndCost,
    returnUpdatedMaterials,
    state.servicesRendered,
    showNotifications,
  ]);

  // useEffect for loading data
  useEffect(() => {
    if (!state.loaded) {
      console.log('hi we have not loaded yet');
      load();
    }
    if (state.pendingSave) {
      dispatch({ type: 'SET_PENDING_SAVE', payload: false });
      saveServiceCall();
    }
    if (state.pendingSave && state.tabIdx === 0 && requestRef.current) {
      // @ts-expect-error ref is useRef(null); click() is valid when a target is attached
      requestRef.current.click();
    }
  }, [state.loaded, load, state.pendingSave, saveServiceCall, state.tabIdx, requestRef, eventId]);

  const handleSetRequestfields = useCallback(
    (fields: string[]) => {
      dispatch({
        type: 'SET_REQUEST_FIELDS',
        payload: [...state.requestFields, ...fields],
      });
    },
    [state.requestFields],
  );
  /*
  const handleChangeEntry = useCallback((data: Event) => {
    updateServiceCallState({
      type: 'setChangeEntry',
      data: {
        entry: data,
        pendingSave: false,
      },
    });
  }, []);
  */

  const jobTypeOptions: Option[] = state.jobTypes.map((id) => ({
    label: id.name,
    value: id.id,
  }));

  const subtypeOptions = buildSubtypeOptions({
    departmentId: state.entry.departmentId,
    jobTypeId: state.entry.jobTypeId,
    subtypes: state.jobSubtypes,
    links: state.jobTypeSubtypes,
    placeholderLabel: OPTION_BLANK,
    requiresSubtype,
    selectedId: savedEntry?.jobSubtypeId ?? 0,
  });
  // Grouped by the trade each Sub Type routes to; Field renders a header per group.
  const jobSubtypeOptions: Option[] = subtypeTradeGroups
    ? groupSubtypeOptions(subtypeOptions, subtypeTradesQuery.data).flatMap((group) =>
        group.options.map((opt) => ({ ...opt, group: group.label })),
      )
    : subtypeOptions;
  const updateStatusForm = useCallback(
    (status: string, options?: { logDateCompleted?: string }) => {
      const entry = Event.clone(state.entry);
      entry.logJobStatus = status;
      if (options?.logDateCompleted != null) {
        entry.logDateCompleted = options.logDateCompleted;
      }
      dispatch({ type: 'SET_ENTRY', payload: entry });
    },
    [state.entry],
  );
  //const { id, logJobNumber, contractNumber } = entry;
  /*
  const {
    firstname,
    lastname,
    businessname,
    phone,
    altphone,
    cellphone,
    fax,
    email,
    billingTerms,
    notification,
  } = customer;

  const { address, city, state, zip } = property;
  */

  const handleSetServicesRenderedPayment = useCallback((payload: ServicesRenderedPaymentType) => {
    console.log({ payload });

    dispatch({ type: 'SET_SERVICES_RENDERED_PAYMENT', payload });
  }, []);

  // Services state callback functions to persist across tab switches
  const handleSetServicesPendingQuotable = useCallback((payload: Quotable[]) => {
    dispatch({ type: 'SET_SERVICES_PENDING_QUOTABLE', payload });
  }, []);

  const handleSetServicesPendingNewQuotable = useCallback((payload: Quotable[]) => {
    dispatch({ type: 'SET_SERVICES_PENDING_NEW_QUOTABLE', payload });
  }, []);

  const handleSetServicesCreatingQuote = useCallback((payload: boolean) => {
    dispatch({ type: 'SET_SERVICES_CREATING_QUOTE', payload });
  }, []);

  const handleSetServicesEditing = useCallback((payload: ServicesRenderedPaymentType) => {
    dispatch({ type: 'SET_SERVICES_EDITING', payload });
  }, []);

  // Initialize tab based on defaultTab parameter, fallback to 'Requests' if invalid index
  const [tab, setTab] = useState(() => getTabValueFromIndex(defaultTab));

  const data: Data = useMemo(
    () => [
      [
        {
          label: 'Customer',
          value: <CopyDataAction data={firstname + ' ' + lastname} />,
        },
        {
          label: 'Business Name',
          value: <CopyDataAction data={businessname} />,
        },
      ],
      [
        {
          label: 'Primary Phone',
          value: <CopyDataAction data={phone} href={true} hrefTag="tel" />,
        },
        {
          label: 'Alternate Phone',
          value: <CopyDataAction data={altphone} href={true} hrefTag="tel" />,
        },
      ],
      [
        {
          label: 'Cell Phone',
          value: <CopyDataAction data={cellphone} href={true} hrefTag="tel" />,
        },
        {
          label: 'Fax',
          value: <CopyDataAction data={fax} href={true} hrefTag="tel" />,
        },
      ],
      [
        {
          label: 'Billing Terms',
          value: <CopyDataAction data={billingTerms} />,
        },
        {
          label: 'Email',
          value: <CopyDataAction data={email} href={true} hrefTag="mailto" />,
        },
      ],
      [
        {
          label: 'Property',
          value: <CopyDataAction data={address} />,
        },
        {
          label: 'City, State, Zip',
          value: <CopyDataAction data={city + ' ' + propertyState + ' ' + zip} />,
        },
      ],
      ...(state.serviceCallId
        ? [
            [
              {
                label: 'Job Number',
                value: <CopyDataAction data={logJobNumber} />,
              },
              {
                label: 'Contract Number',
                value: (
                  <div className="flex items-center gap-2">
                    {state.customer.id > 0 &&
                      state.serviceCallId > 0 &&
                      loggedUserQuery.data?.isNewContractUser && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setOpenContractDialog(true)}
                          title="Add or update the contract number for this job"
                        >
                          Add/Update Contract Number
                        </Button>
                      )}
                    <CopyDataAction data={contractNumber} />
                  </div>
                ),
              },
            ],
            [
              {
                label: 'Job Completion Date',
                value: (
                  <CopyDataAction
                    data={
                      state.entry.logDateCompleted &&
                      state.entry.logDateCompleted.trim() !== '' &&
                      state.entry.logDateCompleted !== NULL_TIME &&
                      !isNaN(new Date(state.entry.logDateCompleted).getTime())
                        ? format(new Date(state.entry.logDateCompleted), 'yyyy-MM-dd')
                        : 'Not Complete'
                    }
                  />
                ),
              },
              {
                label: '',
                value: '',
              },
            ],
          ]
        : []),
    ],
    [
      address,
      altphone,
      billingTerms,
      businessname,
      cellphone,
      city,
      contractNumber,
      email,
      fax,
      firstname,
      lastname,
      logJobNumber,
      loggedUserQuery.data?.isNewContractUser,
      state.customer.id,
      phone,
      propertyState,
      state.serviceCallId,
      state.entry.logDateCompleted,
      zip,
    ],
  );
  /*
  const handleSetPropStoredQuotes = useCallback(
    (data: StoredQuote[]) => {
      dispatch({
        type: 'SET_STORED_QUOTE',
        payload: data,
      });
    },
    [dispatch],
  );

  const onFormChange = useCallback(
    (payload: ProposalsForm) => {
      dispatch({
        type: 'SET_PROPOSALS_FORM',
        payload,
      });
    },
    [dispatch],
  );
*/
  const setEntry = useCallback(
    (data: Event) => {
      dispatch({ type: 'SET_ENTRY', payload: data });
    },
    [dispatch],
  );

  const onRequestChange = useCallback<ComponentProps<typeof Request>['onChange']>(
    (data) => {
      const filteredData: Partial<Event> = Object.keys(data).reduce((filtered, key) => {
        if (
          requestValidateFields.includes(key) &&
          data[key as keyof Event] === state.entry[key as keyof Event]
        ) {
          // @ts-expect-error dynamic string key into Partial<Event> accumulator
          filtered[key] = data[key as keyof Event];
        }
        return filtered;
      }, {});
      const fields: string[] = Object.keys(filteredData);
      const { requestErrors, invoiceErrors } = handleCheckRequest(
        data,
        fields,
        // The answer in hand is the previous department's; the department rule applies until the new one arrives
        data.departmentId === state.entry.departmentId ? requiresSubtype : undefined,
        savedEntry,
      );
      dispatch({
        type: 'SET_REQUEST_ERRORS',
        payload: requestErrors,
      });
      dispatch({
        type: 'SET_INVOICE_ERRORS',
        payload: invoiceErrors,
      });
      // MERGE_ENTRY so fields not in the Request schema (e.g. logNotes, now display-only) survive form edits
      dispatch({ type: 'MERGE_ENTRY', payload: data });
    },
    [state.entry, requiresSubtype, savedEntry],
  );

  const handleViewAllNotes = useCallback(() => setTab('Notes'), []);

  const actions = useMemo(
    () => [
      {
        label: 'Save Job Only',
        onClick: handleSave,
        disabled: state.loading || state.saving,
      },
      ...(state.serviceCallId
        ? [
            {
              label: 'Save and Invoice',
              onClick: handleSaveAndInvoice,
              disabled:
                state.loading ||
                state.saving ||
                !loggedUserQuery.data?.hasBillingAccess ||
                state.entry.logJobStatus === 'Archived',
            },
          ]
        : []),
    ],
    [
      handleSave,
      handleSaveAndInvoice,
      loggedUserQuery.data?.hasBillingAccess,
      state.entry.logJobStatus,
      state.loading,
      state.saving,
      state.serviceCallId,
    ],
  );

  const toggleOpenSellSheet = () => {
    setOpenSellSheet(!openSellSheet);
  };

  const handleFlowChange = (value: string) => {
    if (value === 'new' && eventId > 0) {
      navigate({ to: '/job-details/$eventId', params: { eventId }, search: { flow: 'new' } });
    }
  };

  useEffect(() => {
    if (!state.loading && onLoaded) {
      onLoaded();
    }
  }, [state.loading, onLoaded]);

  return state.loading ? (
    <div className="AddServiceCallContent-loader">
      <Loader customStyles />
    </div>
  ) : state.jobNotFound ? (
    <JobNotFound jobNumber={isNaN(eventId) ? 0 : eventId || 0} />
  ) : (
    <>
      <div className="flex flex-col items-center justify-between gap-2 bg-gray-200 p-2 md:flex-row">
        <div className="flex min-w-max flex-wrap items-center gap-2">
          <h2 className="text-lg md:text-2xl">{asProject ? 'Project Details' : 'Job Details'}</h2>
          {/* The stored, booking-time answer, not a recomputation: this is what
              was true of the property when the job was booked. The dispatch board
              derives its own badge from today's boundary instead. */}
          {state.entry.isOutsideServiceArea === 1 && (
            <span className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800">
              <MapPin className="h-3 w-3 shrink-0" />
              Outside service area
            </span>
          )}
        </div>
        {eventId > 0 || state.serviceCallId ? (
          <div className="flex flex-wrap items-center justify-center gap-2 md:justify-end">
            {state.serviceCallId ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  if (onClose) {
                    onClose();
                  } else {
                    navigate({
                      to: '/properties/$propertyId',
                      params: { propertyId: state.entry.propertyId },
                    });
                  }
                }}
              >
                Close
              </Button>
            ) : null}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="outline">
                  <MoreVertical className="h-4 w-4" />
                  <span className="ml-2">Actions</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                {eventId > 0 ? (
                  <>
                    <DropdownMenuLabel className="text-muted-foreground text-xs font-normal">
                      Job view
                    </DropdownMenuLabel>
                    <DropdownMenuItem disabled className="opacity-100">
                      <Check className="mr-2 h-4 w-4" />
                      Admin View
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleFlowChange('new')}>
                      Field View
                    </DropdownMenuItem>
                    {state.serviceCallId ? <DropdownMenuSeparator /> : null}
                  </>
                ) : null}
                {state.serviceCallId ? (
                  <>
                    <DropdownMenuItem onClick={toggleOpenSpiffApply} disabled={state.loading}>
                      Spiff Apply
                    </DropdownMenuItem>
                    {loggedUserQuery.data?.isWorkPieceAdmin &&
                      !workPieceQuery.data?.results.length && (
                        <DropdownMenuItem
                          onClick={toggleOpenPieceWorkApply}
                          disabled={state.loading}
                        >
                          Piece Work Apply
                        </DropdownMenuItem>
                      )}
                    <DropdownMenuItem
                      onClick={toggleOpenCallbackReview}
                      disabled={!state.entry.isCallback}
                    >
                      Callback Review
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={toggleOpenJobActivity} disabled={state.loading}>
                      Job Activity
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={toggleOpenSellSheet} disabled={state.loading}>
                      Create Sell Sheet
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => notificationTriggerRef.current?.click()}>
                      {customerQuery.data?.notification ? 'View Notification' : 'Add Notification'}
                    </DropdownMenuItem>
                  </>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ) : null}
      </div>

      <InfoTable data={data} error={state.error} className="JobDetailsTable" />

      <SectionBar title="Job Data" actions={actions} />

      {billingTerms === 'COD' &&
        !['Paid', 'Paid on contract'].includes(state.entry.logPaymentStatus) && (
          <Alert variant="destructive" className="border-destructive mb-4 border-2">
            <AlertTitle className="text-xl font-bold tracking-tight">COD Customer</AlertTitle>
            <AlertDescription className="text-lg font-semibold">
              This customer is flagged as Cash on Delivery (COD). Please ensure payment is
              collected.
            </AlertDescription>
          </Alert>
        )}

      {state.customer.employeeDepartmentID !== 0 && (
        <Alert variant="default" className="border-primary mb-4 border-2 bg-blue-50">
          <AlertTitle className="text-xl font-bold tracking-tight">
            {state.customer.employeeDepartmentID === 1
              ? 'Flat Rate Pricing'
              : 'Time and Materials Pricing'}
          </AlertTitle>
          <AlertDescription className="text-lg font-semibold">
            {state.customer.employeeDepartmentID === 1
              ? 'This customer is on a Flat Rate pricing model.'
              : `This customer is billed hourly at ${formatHourlyRate(customerHourlyRate(state.customer))}/hour for Time and Materials.`}
          </AlertDescription>
        </Alert>
      )}

      <Tabs
        value={tab}
        onValueChange={setTab}
        defaultValue="Requests"
        className="mt-4 flex w-full flex-col"
      >
        <TabsList className="bg-foreground/10 mx-auto flex h-max flex-wrap">
          <TabsTrigger value="Requests">Requests</TabsTrigger>
          {!!state.serviceCallId && (
            <>
              <TabsTrigger value="PropertyHistory">Property History</TabsTrigger>
              <TabsTrigger value="Equipment">Equipment</TabsTrigger>
              <TabsTrigger value="Services">Services</TabsTrigger>
              <TabsTrigger value="Notes">Notes</TabsTrigger>
              <TabsTrigger value="NewProposal">Proposal</TabsTrigger>
              {loggedUserQuery.data?.isNewProposalTester && (
                <TabsTrigger value="CustomerProposal">Customer Proposal</TabsTrigger>
              )}
              <TabsTrigger value="Invoice">Legacy Invoice</TabsTrigger>
              {loggedUserQuery.data?.permissionGroups.find(({ name }) => name === 'DevTesting') &&
                !!state.invoiceData?.id && (
                  <TabsTrigger value="PaymentLink">Payment Link</TabsTrigger>
                )}
              {!!state.customer.id && !!eventId && !!state.invoiceData?.id && (
                <TabsTrigger value="InvoicePayment">Payments</TabsTrigger>
              )}
              {loggedUserQuery.data?.isSpiffAdmin && (
                <TabsTrigger value="Spiffs">Spiffs</TabsTrigger>
              )}
              {loggedUserQuery.data?.isWorkPieceAdmin && (
                <TabsTrigger value="PieceWorkOptions">Piece Work Options</TabsTrigger>
              )}
            </>
          )}
        </TabsList>
        <TabsContent value="Requests">
          <Request
            key={`${state.loading}-${state.entry.logJobStatus}`}
            className="Service-Request"
            serviceItem={state.entry}
            propertyEvents={state.propertyEvents}
            loading={state.loading}
            jobTypeOptions={jobTypeOptions}
            jobSubtypeOptions={jobSubtypeOptions}
            tradeName={tradeResolution?.name}
            subtypeRequired={isSubtypeRequired(state.entry.departmentId, requiresSubtype)}
            canArchive={loggedUserQuery.data?.isArchiveAdmin}
            isManager={!!loggedUserQuery.data?.permissionGroups.find((pg) => pg.name === 'Manager')}
            customerId={state.customer.id}
            onChange={onRequestChange}
            disabled={state.saving}
            formErrors={state.requestErrors}
            onInitSchema={handleSetRequestfields}
            onViewAllNotes={handleViewAllNotes}
          />
        </TabsContent>
        {state.serviceCallId && (
          <>
            <TabsContent value="Equipment">
              <Equipment
                propertyId={propertyId || state.entry.propertyId}
                event={state.entry}
                onSelectedChange={(value: string) => {
                  dispatch({
                    type: 'SET_ENTRY',
                    payload: { ...state.entry, invoiceServiceItem: value },
                  });
                }}
              />
            </TabsContent>
            <TabsContent value="Services">
              {loggedUserQuery.isSuccess ? (
                <Services
                  serviceRenderedPayment={state.servicesRenderedPayment}
                  serviceCallId={state.serviceCallId}
                  currentStatus={state.entry.logJobStatus}
                  archived={state.entry.logJobStatus === 'Archived'}
                  servicesRenderedProp={state.servicesRendered}
                  loggedUser={loggedUserQuery.data}
                  updateServicesRenderedProp={updateServicesRenderedFromProp}
                  paidServicesFetch={loadPayments}
                  loading={state.loading}
                  updateStatus={updateStatusForm}
                  onUpdateMaterials={handleUpdateMaterialsStringAndCost}
                  updateServiceRenderedPayment={handleSetServicesRenderedPayment}
                  department={state.entry.departmentId?.toString()}
                  servicesPendingQuotable={state.servicesPendingQuotable}
                  servicesPendingNewQuotable={state.servicesPendingNewQuotable}
                  servicesCreatingQuote={state.servicesCreatingQuote}
                  servicesEditing={state.servicesEditing}
                  onSetServicesPendingQuotable={handleSetServicesPendingQuotable}
                  onSetServicesPendingNewQuotable={handleSetServicesPendingNewQuotable}
                  onSetServicesCreatingQuote={handleSetServicesCreatingQuote}
                  onSetServicesEditing={handleSetServicesEditing}
                />
              ) : (
                <InfoTable data={makeFakeRows(4, 4)} loading />
              )}
            </TabsContent>
            <TabsContent value="Invoice">
              {state.loading ? (
                <InfoTable data={makeFakeRows(4, 5)} loading />
              ) : (
                <Invoice
                  event={state.entry}
                  showBilling={!!loggedUserQuery.data?.hasBillingAccess}
                  onChangeServices={setEntry}
                  payments={state.payments}
                  onChangePayment={setEntry}
                  disabled={state.saving}
                  servicesRendered={state.servicesRendered}
                  onInitSchema={handleSetRequestfields}
                  formErrors={state.invoiceErrors}
                  excludedMaterialTotal={state.excludedMaterialTotal}
                />
              )}
            </TabsContent>
            {loggedUserQuery.data?.permissionGroups.find(({ name }) => name === 'DevTesting') &&
              !!state.invoiceData?.id && (
                <TabsContent value="PaymentLink">
                  <div className="p-4">
                    <InvoicePaymentLinkGenerator
                      invoiceId={state.invoiceData.id}
                      jobNumber={state.entry.logJobNumber}
                    />
                  </div>
                </TabsContent>
              )}
            {/*
            <TabsContent value="Proposals">
              <div key={'ProposalTab'}>
                <Proposal
                  serviceItem={state.entry}
                  servicesRendered={state.servicesRendered}
                  customer={state.customer}
                  property={state.property}
                  propStoredQuotes={state.storedQuote}
                  initalForm={state.proposalsForm}
                  handleSetPropStoredQuotes={handleSetPropStoredQuotes}
                  reload={load}
                  onFormChange={onFormChange}
                />
              </div>
            </TabsContent>
*/}

            <TabsContent value="NewProposal">
              <div key={'NewProposalTab'}>
                <ProposalProvider
                  eventId={state.entry.id}
                  event={state.entry}
                  customer={state.customer}
                  property={state.property}
                >
                  <ProposalModule
                    serviceItem={state.entry}
                    servicesRendered={state.servicesRendered}
                    customer={state.customer}
                    property={state.property}
                    reload={load}
                    onEventUpdate={(updates) => dispatch({ type: 'MERGE_ENTRY', payload: updates })}
                  />
                </ProposalProvider>
              </div>
            </TabsContent>

            {loggedUserQuery.data?.isNewProposalTester && (
              <TabsContent value="CustomerProposal">
                <div key={'CustomerProposalTab'}>
                  <CustomerProposalWrapperByEvent serviceItem={state.entry} reload={load} />
                </div>
              </TabsContent>
            )}

            <TabsContent value="Notes">
              <Notes jobId={state.serviceCallId} showExport />
            </TabsContent>

            {loggedUserQuery.data?.isSpiffAdmin && (
              <TabsContent value="Spiffs">
                <Spiffs serviceItem={state.entry} />
              </TabsContent>
            )}

            <TabsContent value="PieceWorkOptions">
              <PieceWorkOptionsTable eventId={eventId ?? 0} />
            </TabsContent>

            {!!state.customer.id && !!eventId && !!state.invoiceData?.id && (
              <TabsContent value="InvoicePayment">
                <InvoicePaymentModule
                  invoiceId={state.invoiceData.id}
                  customerId={state.customer.id}
                />
              </TabsContent>
            )}

            <TabsContent value="PropertyHistory">
              <PropertyHistoryWrapper
                propertyId={propertyId || state.entry.propertyId}
                currentEventId={state.serviceCallId}
              />
            </TabsContent>
          </>
        )}
      </Tabs>
      {state.openJobActivity && eventId && eventId != 0 && (
        <ServiceCallLogs
          open
          eventId={eventId}
          membershipId={state.entry.membershipId}
          onClose={() => toggleOpenJobActivity()}
        />
      )}
      {state.openSpiffApply && state.serviceCallId != 0 && (
        <SpiffApplyComponent
          loggedUserId={loggedUserId!}
          serviceCallId={state.serviceCallId}
          onClose={() => toggleOpenSpiffApply()}
        />
      )}
      <Dialog onOpenChange={() => toggleOpenPieceWorkApply()} open={state.openPieceWorkApply}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Piece Work Spiff</DialogTitle>
          </DialogHeader>
          <PieceWorkApplyForm
            onMissingOptionAlertCtaClick={() => {
              setTab('PieceWorkOptions');
              setCreateSpiffDialogStore(true);
            }}
            onSubmit={() => toggleOpenPieceWorkApply()}
            onClose={() => toggleOpenPieceWorkApply()}
            eventId={state.serviceCallId}
          />
        </DialogContent>
      </Dialog>

      {state.openCallbackReview && state.serviceCallId != 0 && (
        <CallbackReview
          loggedUserId={loggedUserId!}
          event={state.entry}
          onClose={() => toggleOpenCallbackReview()}
        />
      )}
      {state.emailAlertOpen && state.serviceCallId != 0 && (
        <Modal
          open={state.emailAlertOpen}
          onClose={() => {
            dispatch({ type: 'SET_EMAIL_ALERT_OPEN', payload: false });
          }}
        >
          <Form
            title="Confirm Invoice"
            key={state.serviceCallId + 'Invoice'}
            submitLabel="Invoice"
            schema={SCHEMA_EMAIL_INVOICE}
            onClose={() => {
              dispatch({ type: 'SET_EMAIL_ALERT_OPEN', payload: false });
            }}
            onSave={(sendInvoice) => {
              dispatch({
                type: 'SET_SEND_INVOICE_TO_CUSTOMER',
                payload: sendInvoice.emailInvoice,
              });
              dispatch({
                type: 'SET_PAYMENT_PROCESSOR',
                payload: sendInvoice.paymentProcessor ?? 'helcim',
              });
              dispatch({ type: 'SET_SAVE_INVOICE', payload: true });
              dispatch({ type: 'SET_EMAIL_ALERT_OPEN', payload: false });
              dispatch({ type: 'SET_PENDING_SAVE', payload: true });
            }}
            data={{
              emailInvoice: state.sendInvoiceToCustomer,
              paymentProcessor: state.paymentProcessor,
            }}
          ></Form>
        </Modal>
      )}
      {openSellSheet && (
        <Modal open onClose={toggleOpenSellSheet}>
          <SellSheet
            propertyId={propertyId || state.entry.propertyId}
            jobNumber={state.serviceCallId}
            ownerId={loggedUserId}
            onCreateDoc={toggleOpenSellSheet}
            onClose={toggleOpenSellSheet}
          />
        </Modal>
      )}

      <ContractNumberDialog
        open={openContractDialog && state.customer.id > 0 && state.serviceCallId > 0}
        onClose={() => setOpenContractDialog(false)}
        event={state.entry}
        customerId={state.customer.id}
        onSuccess={() => {
          // Refresh the job page by reloading the data
          load();
        }}
      />

      <CustomerNotificationDialog
        customerId={state.customer.id}
        showOnMount={showNotifications}
        trigger={<button ref={notificationTriggerRef} className="hidden" />}
        bottomContent={
          state.property.notification ? (
            <div className="mt-4 flex flex-col gap-2">
              <h2 className="text-lg font-semibold">Property notification</h2>
              {state.property.notification}
            </div>
          ) : undefined
        }
      />
    </>
  );
};
